import { useEffect, useState } from "react";
import { api, type AdminUser } from "../lib/api";
import { useAuthCtx } from "../hooks/AuthContext";

export function AdminUsers() {
  const { me } = useAuthCtx();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setUsers(await api.listUsers());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.createUser(email.trim().toLowerCase(), password, isAdmin);
      setEmail(""); setPassword(""); setIsAdmin(false);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onResetPassword = async (u: AdminUser) => {
    const next = window.prompt(`New password for ${u.email} (min 8 chars):`);
    if (!next) return;
    try {
      await api.updateUser(u.id, { password: next });
      alert(`Password updated for ${u.email}. Share this with them: ${next}`);
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const onToggleAdmin = async (u: AdminUser) => {
    try {
      await api.updateUser(u.id, { is_admin: !u.is_admin });
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const onDelete = async (u: AdminUser) => {
    if (!confirm(`Delete user ${u.email}? This refuses if they have any coffees.`)) return;
    try {
      await api.deleteUser(u.id);
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <div className="space-y-1">
        <div className="eyebrow">Admin</div>
        <h1 className="font-display text-2xl">Users</h1>
        <p className="text-xs text-paper-500">Each user gets a fully separated coffee vault. Share the temp password out-of-band; they can change it from Account.</p>
      </div>

      <form onSubmit={onCreate} className="border border-paper-200 rounded-md p-4 space-y-4 bg-white">
        <h2 className="text-sm uppercase tracking-widest text-paper-600">Add user</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
          </div>
          <div>
            <label className="label">Temp password</label>
            <input type="text" className="input" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required autoComplete="off" />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
          Administrator
        </label>
        <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Adding…" : "Add user"}</button>
      </form>

      {error ? <div className="text-red-700 text-xs">{error}</div> : null}

      <div className="border border-paper-200 rounded-md bg-white divide-y divide-paper-200">
        {loading ? (
          <div className="p-6 text-sm text-paper-500">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-6 text-sm text-paper-500">No users.</div>
        ) : (
          users.map((u) => (
            <div key={u.id} className="p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{u.email}</div>
                <div className="text-xs text-paper-500">{u.is_admin ? "Admin" : "Member"} · joined {u.created_at.slice(0, 10)}</div>
              </div>
              <button onClick={() => onResetPassword(u)} className="text-[11px] uppercase tracking-widest text-paper-600 hover:text-ink">
                Reset password
              </button>
              <button onClick={() => onToggleAdmin(u)} className="text-[11px] uppercase tracking-widest text-paper-600 hover:text-ink">
                {u.is_admin ? "Demote" : "Make admin"}
              </button>
              {me?.email !== u.email ? (
                <button onClick={() => onDelete(u)} className="text-[11px] uppercase tracking-widest text-red-600 hover:text-red-800">
                  Delete
                </button>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
